import type React from "react";
import { useMemo, useState } from "react";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { faCalendarLinesPen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, IconButton } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { paginationModel, usePagination } from "api/hooks/usePagination";
import { useGetPendingTreatmentActivityLogs } from "api/services/admin/hooks";
import { WidgetBox } from "shared/containers/WidgetBox";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { useLocalizedDate } from "utils/date";

interface PendingTreatmentActivityLogsProps {
  hcoID: number;
}

export const PendingTreatmentActivityLogs: React.FC<PendingTreatmentActivityLogsProps> = ({ hcoID }) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();
  const { pagination, setPagination } = usePagination();
  const [expanded, setExpanded] = useState(true);

  const { data: pendingTreatmentActivityLogsData, isLoading } = useGetPendingTreatmentActivityLogs({
    health_care_organization_id: hcoID,
    params: {
      limit: pagination.limit,
      page: pagination.page + 1,
    },
  });
  const pendingTreatmentActivityLogs = pendingTreatmentActivityLogsData?.items || [];

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "patient_id",
        headerName: t("admin.pending_treatment_activity_logs.patient_id"),
        flex: 1,
      },
      {
        field: "patient_name",
        headerName: t("admin.pending_treatment_activity_logs.patient_name"),
        flex: 1,
        minWidth: 160,
      },
      {
        field: "therapist_name",
        headerName: t("admin.pending_treatment_activity_logs.therapist_name"),
        flex: 1,
        minWidth: 160,
      },
      {
        field: "category",
        headerName: t("admin.pending_treatment_activity_logs.category"),
        valueFormatter: (value: { description: string }) => value?.description ?? "",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "start_time",
        headerName: t("admin.pending_treatment_activity_logs.start_time"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), "PP") : ""),
        flex: 1,
        minWidth: 140,
      },
      {
        field: "duration",
        headerName: t("admin.pending_treatment_activity_logs.duration"),
        valueFormatter: (value: number) => t("patients.activity_log.table.duration", { duration: value / 60 }),
        flex: 1,
        minWidth: 100,
      },
      {
        field: "status",
        headerName: t("admin.pending_treatment_activity_logs.status"),
        flex: 1,
        minWidth: 120,
      },
    ],
    [t]
  );

  return (
    <>
      <WidgetBox
        title={t("admin.pending_treatment_activity_logs.title")}
        description={t("admin.pending_treatment_activity_logs.description", {
          count: pendingTreatmentActivityLogsData?.metadata?.total_count ?? 0,
        })}
        icon={<FontAwesomeIcon icon={faCalendarLinesPen} />}
        color="blue"
        toolbar={
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ fontSize: 14 }} />
          </IconButton>
        }
        skipPadding
      >
        <Collapse in={expanded}>
          <MUIDataGrid
            columns={columns}
            rows={pendingTreatmentActivityLogs}
            getRowId={row => row.id}
            loading={isLoading}
            initialState={{ pagination: { paginationModel } }}
            paginationMode="server"
            onPaginationModelChange={model => setPagination({ limit: model.pageSize, page: model.page })}
            rowCount={pendingTreatmentActivityLogsData?.metadata.total_count ?? 0}
            rowSelection={false}
            slots={{
              noRowsOverlay: () => <EmptyResultOverlay />,
              noResultsOverlay: () => <EmptyResultOverlay />,
            }}
            localeText={{
              noRowsLabel: "",
              noResultsOverlayLabel: "",
            }}
            disableColumnMenu
            hideHeader
            transparentBackground
            density="comfortable"
            sx={{
              "& .MuiDataGrid-row": {
                cursor: "default",
              },
            }}
          />
        </Collapse>
      </WidgetBox>
    </>
  );
};
